import { injectable } from 'inversify';
import { PJSONPOptions } from 'pjsonp';
import { Address as GenericAddress } from '../interfaces/generic/Address';
import { GeocoderEntry } from '../interfaces/search/GeocoderEntry';
import { GeocodingRequestData } from '../interfaces/state-events/GeocodingRequest';
import {
  Address,
  CoordFormatOut,
  Credentials,
  Location,
  Position
} from '../interfaces/webservices/core-api-v2/General';
import {
  GeocodeLocationRequest,
  ReverseGeocodePositionRequest
} from '../interfaces/webservices/core-api-v2/Request/GeoServices';
import { GeocodeAddressRequest } from '../interfaces/webservices/core-api-v2/Request/GeoServices';
import { GeoItem } from '../interfaces/webservices/core-api-v2/Response/AddressSearches';
import { GeoServicesResult } from '../interfaces/webservices/core-api-v2/Response/GeoServicesResult';
import { Geocoding } from '../interfaces/webservices/Geocoding';
import { isAddress, isLatLngLiteral, isSingleSlotAddress } from './guards';
import pjsonp from './vendor/pjsonp';
import { getConfig } from '../ConfigLoader';

@injectable()
export class RequestGeocoder implements Geocoding {
  public static get url(): string {
    return `${ym.options.rootUrl}api_js/V2/GeoServices.asmx/`;
  }

  public static get credentials(): Credentials {
    return {
      SystemPartner: ym.options.name || '',
      SecurityID: ym.options.securityID || '',
      Channel: ym.options.channel || ''
    };
  }

  public static get jsonpOptions(): PJSONPOptions {
    return {
      prefix: 'YMFF__'
    };
  }

  public static convertGeocoderItemMap(item: GeoItem): GeocoderEntry {
    const result: GeocoderEntry = {
      type: 'Geocoder',
      position: {
        lat: parseFloat(item.LocY || '0'),
        lng: parseFloat(item.LocX || '0'),
        alt: undefined
      },
      address: {
        street: item.Street,
        houseNo: item.HouseNo,
        zip: item.Zip,
        city: item.City,
        cityAddon: item.CityAddOn,
        cityPart: item.CityPart,
        country: item.Country,
        district: item.District,
        isoCountry: item.Country
      }
    };
    return result;
  }

  private static createGeocodeString(input: GeocodingRequestData): Location {
    return {
      Location: input.singleSlot || ''
    };
  }

  private static createGeocodeAddress(input: GeocodingRequestData): Address {
    return {
      City: input.city || '',
      Zip: input.zip || '',
      Street: input.streetNo || ''
    };
  }

  private static createReverseGeocode(input: GeocodingRequestData): Position {
    const position: Position = {
      CoordFormatIn: 'GEODECIMAL_POINT',
      LocX: 0,
      LocY: 0
    };
    if (isLatLngLiteral(input.position)) {
      position.LocX = input.position.lng;
      position.LocY = input.position.lat;
    }
    return position;
  }

  public async geocode(input: GeocodingRequestData): Promise<GeocoderEntry[]> {
    const config = getConfig();
    const { url, credentials, jsonpOptions } = RequestGeocoder;
    let requestData: Credentials & CoordFormatOut = {
      ...credentials,
      CoordFormatOut: 'GEODECIMAL_POINT'
    };
    let method = '';
    if (isLatLngLiteral(input.position)) {
      method = 'ReverseGeocodePosition';
      requestData = {
        ...requestData,
        ...RequestGeocoder.createReverseGeocode(input),
        Addition: ''
      } as ReverseGeocodePositionRequest;
    } else if (isAddress(input)) {
      method = 'GeocodeAddress';
      requestData = {
        ...requestData,
        ...RequestGeocoder.createGeocodeAddress(input),
        IsoCountryCode: input.isoCountry,
        IsoLocale: config.defaultIsoLocale
      } as GeocodeAddressRequest;
    } else if (isSingleSlotAddress(input)) {
      const isoCountry = (input as GenericAddress).isoCountry;
      method = 'GeocodeLocation';
      requestData = {
        ...requestData,
        ...RequestGeocoder.createGeocodeString(input),
        IsoCountryCode: isoCountry
      } as GeocodeLocationRequest;
    }

    if (method) {
      const result: GeoServicesResult = await pjsonp(
        `${url}${method}`,
        requestData,
        jsonpOptions
      );

      if (!result.GeoItems) {
        result.GeoItems = [];
      }

      return result.GeoItems.map(RequestGeocoder.convertGeocoderItemMap);
    }

    return [];
  }
}
